import { LineStyle, createChart, type IChartApi, type ISeriesApi, type UTCTimestamp } from 'lightweight-charts';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Observable, Subscription } from 'rxjs';
import { useTheme } from 'styled-components';

export interface OHLCVData {
  series: OHLCVDatapoint[];
  initial?: boolean;
}

export interface OHLCVDatapoint {
  Timestamp: string;
  Open: string;
  High: string;
  Low: string;
  Close: string;
  Volume: string;
}

export interface CandlesticksChartProps {
  dataObservable: Observable<OHLCVData>;
  width: number;
  height: number;
  compact?: boolean;
  disableInteraction?: boolean;
}

export const CandlesticksChart: React.FC<CandlesticksChartProps> = ({
  dataObservable,
  width,
  height,
  compact = false,
  disableInteraction = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [series, setSeries] = useState<ISeriesApi<'Candlestick'>>();
  const [chart, setChart] = useState<IChartApi>();
  const theme = useTheme();

  useEffect(() => {
    const chart = createChart(ref.current as HTMLDivElement);
    setChart(chart);
    setSeries(chart.addCandlestickSeries());
  }, []);

  // Update chart size
  useEffect(() => {
    if (chart) {
      chart.resize(width, height);
    }
  }, [chart, width, height]);

  // Apply styles to chart
  useEffect(() => {
    const backgroundColor = theme.backgroundContent;
    const textColor = theme.colorTextDefault;
    const priceScaleColor = theme.borderColorChartAxis;
    const gridLineColor = theme.borderColorChartGridLine;
    if (chart) {
      chart.applyOptions({
        layout: {
          background: {
            color: backgroundColor,
          },
          textColor,
          fontSize: compact ? 8 : theme.baseSize * theme.fontSizeSmall,
          fontFamily: theme.fontFamily,
        },
        rightPriceScale: {
          borderColor: priceScaleColor,
        },
        timeScale: {
          borderColor: priceScaleColor,
          timeVisible: true,
          fixRightEdge: true,
        },
        handleScroll: disableInteraction ? false : undefined,
        handleScale: disableInteraction ? false : undefined,
        grid: {
          vertLines: {
            color: gridLineColor,
            style: LineStyle.Solid,
            visible: true,
          },
          horzLines: {
            color: gridLineColor,
            style: LineStyle.Solid,
            visible: true,
          },
        },
        crosshair: {
          vertLine: {
            color: theme.borderColorChartCrosshair,
            style: LineStyle.LargeDashed,
            visible: true,
            labelVisible: true,
          },
          horzLine: {
            color: theme.borderColorChartCrosshair,
            style: LineStyle.LargeDashed,
            visible: true,
            labelVisible: true,
          },
          mode: 1,
        },
      });
    }
  }, [chart, theme, compact, disableInteraction]);

  // Apply styles to series
  useEffect(() => {
    const upColor = theme.colors.green.lighten;
    const downColor = theme.colors.red.lighten;
    if (series) {
      series.applyOptions({
        borderVisible: false,
        upColor,
        wickUpColor: upColor,
        downColor,
        wickDownColor: downColor,
      });
    }
  }, [series, theme]);

  useEffect(() => {
    let sub: Subscription;
    if (dataObservable != null && series != null && chart != null) {
      sub = dataObservable.subscribe(data => {
        const chartData = data.series.map(d => {
          return {
            time: (new Date(d['Timestamp']).getTime() / 1000) as UTCTimestamp,
            open: parseFloat(d['Open']),
            high: parseFloat(d['High']),
            low: parseFloat(d['Low']),
            close: parseFloat(d['Close']),
          };
        });
        if (data.initial) {
          series.setData(chartData);
          chart.timeScale().resetTimeScale();
          chart.timeScale().fitContent();
        } else {
          for (const d of chartData) {
            series.update(d);
          }
        }
      });
    }
    return () => sub?.unsubscribe();
  }, [dataObservable, series, chart]);

  return <div ref={ref} />;
};
